import React, { useState, useEffect, useRef } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  SkipBack,
  SkipForward,
} from "lucide-react";

interface MediaControlsProps {
  mediaRef: HTMLVideoElement | HTMLAudioElement | null;
  type: "video" | "audio";
  onFullscreen?: () => void;
  isFullscreen?: boolean;
  className?: string;
}

const MediaControls: React.FC<MediaControlsProps> = ({
  mediaRef,
  type,
  onFullscreen,
  isFullscreen = false,
  className = "",
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mediaRef) return;

    const handleTimeUpdate = () => setCurrentTime(mediaRef.currentTime);
    const handleDurationChange = () => setDuration(mediaRef.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const handleVolumeChange = () => {
      setVolume(mediaRef.volume);
      setIsMuted(mediaRef.muted);
    };

    mediaRef.addEventListener("timeupdate", handleTimeUpdate);
    mediaRef.addEventListener("durationchange", handleDurationChange);
    mediaRef.addEventListener("play", handlePlay);
    mediaRef.addEventListener("pause", handlePause);
    mediaRef.addEventListener("ended", handleEnded);
    mediaRef.addEventListener("volumechange", handleVolumeChange);

    // Initialize
    if (mediaRef.duration) setDuration(mediaRef.duration);
    if (mediaRef.currentTime) setCurrentTime(mediaRef.currentTime);
    setVolume(mediaRef.volume);
    setIsMuted(mediaRef.muted);

    return () => {
      mediaRef.removeEventListener("timeupdate", handleTimeUpdate);
      mediaRef.removeEventListener("durationchange", handleDurationChange);
      mediaRef.removeEventListener("play", handlePlay);
      mediaRef.removeEventListener("pause", handlePause);
      mediaRef.removeEventListener("ended", handleEnded);
      mediaRef.removeEventListener("volumechange", handleVolumeChange);
    };
  }, [mediaRef]);

  const togglePlayPause = () => {
    if (!mediaRef) return;
    if (isPlaying) {
      mediaRef.pause();
    } else {
      mediaRef.play();
    }
  };

  const toggleMute = () => {
    if (!mediaRef) return;
    mediaRef.muted = !mediaRef.muted;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mediaRef) return;
    const newVolume = parseFloat(e.target.value);
    mediaRef.volume = newVolume;
    if (newVolume > 0 && mediaRef.muted) {
      mediaRef.muted = false;
    }
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mediaRef || !timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    mediaRef.currentTime = percentage * duration;
  };

  const skip = (seconds: number) => {
    if (!mediaRef) return;
    mediaRef.currentTime = Math.max(
      0,
      Math.min(duration, mediaRef.currentTime + seconds)
    );
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={`bg-gray-900 text-white p-3 rounded-lg ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Timeline */}
      <div
        ref={timelineRef}
        className="w-full h-1.5 bg-gray-700 rounded-full cursor-pointer mb-3 relative group"
        onClick={handleTimelineClick}
      >
        <div
          className="h-full bg-pink-500 rounded-full relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        {/* Left controls */}
        <div className="flex items-center gap-2">
          {/* Skip back */}
          <button
            onClick={() => skip(-10)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-800 rounded-full transition-colors"
            title="Skip back 10s"
          >
            <SkipBack size={16} />
          </button>

          {/* Play/Pause */}
          <button
            onClick={togglePlayPause}
            className="w-10 h-10 flex items-center justify-center bg-pink-500 hover:bg-pink-600 rounded-full transition-colors"
          >
            {isPlaying ? <Pause size={18} fill="white" /> : <Play size={18} fill="white" />}
          </button>

          {/* Skip forward */}
          <button
            onClick={() => skip(10)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-800 rounded-full transition-colors"
            title="Skip forward 10s"
          >
            <SkipForward size={16} />
          </button>

          {/* Time */}
          <div className="text-xs text-gray-400 ml-1">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2">
          {/* Volume */}
          <div
            className="relative flex items-center"
            onMouseEnter={() => setShowVolumeSlider(true)}
            onMouseLeave={() => setShowVolumeSlider(false)}
          >
            <button
              onClick={toggleMute}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-800 rounded-full transition-colors"
            >
              {isMuted || volume === 0 ? (
                <VolumeX size={16} />
              ) : (
                <Volume2 size={16} />
              )}
            </button>

            {/* Volume slider */}
            {showVolumeSlider && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-gray-800 p-2 rounded-lg shadow-lg">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="h-20 accent-pink-500 cursor-pointer"
                  style={{
                    WebkitAppearance: "slider-vertical" as any,
                    appearance: "slider-vertical" as any,
                  }}
                />
              </div>
            )}
          </div>

          {/* Fullscreen (video only) */}
          {type === "video" && onFullscreen && (
            <button
              onClick={onFullscreen}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-800 rounded-full transition-colors"
            >
              {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaControls;
